import { InputSwitch } from "primereact/inputswitch";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";

import {
  useCheckNcciBypassAvailableQuery,
  useToggleNcciBypassMutation,
} from "../../store/queries/claims";
import { IClaim } from "../../types/claim";

interface INCCIBypassToggleProps {
  claimId: string;
  claim: IClaim;
  onClaimUpdated: (claim: IClaim) => void;
}

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  margin-bottom: 1rem;
`;

const ToggleLabel = styled.label`
  font-weight: 500;
  color: #495057;
  cursor: pointer;
`;

const BypassInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const BypassDescription = styled.span`
  font-size: 0.875rem;
  color: #6c757d;
`;

const BypassBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: #e8f5e9;
  color: #2e7d32;
  margin-left: 0.5rem;
`;

const LoadingContainer = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 0.5rem;
`;

export function NCCIBypassToggle({
  claimId,
  claim,
  onClaimUpdated,
}: INCCIBypassToggleProps) {
  const toastRef = useRef<Toast>(null);
  const [isEnabled, setIsEnabled] = useState(claim.ncciBypassEnabled ?? false);

  // Check if NCCI bypass is available for this claim
  const { data: availabilityData, isLoading: isCheckingAvailability } =
    useCheckNcciBypassAvailableQuery({ claimId }, { skip: !claimId });

  const [toggleNcciBypass, { isLoading: isToggling }] =
    useToggleNcciBypassMutation();

  // Sync local state with claim data when it changes
  useEffect(() => {
    setIsEnabled(claim.ncciBypassEnabled ?? false);
  }, [claim.ncciBypassEnabled]);

  const isAvailable = availabilityData?.data?.ncciBypassAvailable ?? false;

  // Don't render anything if bypass is not available
  if (!isCheckingAvailability && !isAvailable) {
    return null;
  }

  const handleToggle = async () => {
    const newValue = !isEnabled;

    try {
      const result = await toggleNcciBypass({
        claimId,
        enabled: newValue,
      }).unwrap();

      // Update local state
      setIsEnabled(newValue);

      // Notify parent of updated claim
      if (result.data?.claim) {
        onClaimUpdated(result.data.claim);
      }

      toastRef.current?.show({
        severity: "success",
        summary: newValue ? "NCCI Bypass Enabled" : "NCCI Bypass Disabled",
        detail: newValue
          ? `NCCI bypass has been applied. Expected amount updated by $${
              (result.data?.newAmountDifference ?? 0) / 100
            }.`
          : "NCCI bypass has been removed. Expected amount reverted to original.",
        life: 5000,
      });
    } catch (error: any) {
      toastRef.current?.show({
        severity: "error",
        summary: "Failed to Toggle NCCI Bypass",
        detail:
          error?.data?.message ||
          "An error occurred while updating NCCI bypass status.",
        life: 5000,
      });
      // Revert local state on error
      setIsEnabled(!newValue);
    }
  };

  return (
    <>
      <Toast ref={toastRef} />
      <ToggleContainer>
        <BypassInfo>
          <div>
            <ToggleLabel htmlFor="ncci-bypass-toggle">
              Apply NCCI Bypass
            </ToggleLabel>
            {isEnabled && (
              <BypassBadge>
                <i className="pi pi-check" style={{ fontSize: "0.7rem" }} />
                NCCI bypass applied
              </BypassBadge>
            )}
            {(isCheckingAvailability || isToggling) && (
              <LoadingContainer>
                <ProgressSpinner
                  style={{ width: "16px", height: "16px" }}
                  strokeWidth="4"
                />
              </LoadingContainer>
            )}
          </div>
          <BypassDescription>
            An NCCI edit was detected for this claim. Enable bypass to restore
            payment for bundled codes.
          </BypassDescription>
        </BypassInfo>
        <InputSwitch
          id="ncci-bypass-toggle"
          checked={isEnabled}
          onChange={handleToggle}
          disabled={isCheckingAvailability || isToggling}
        />
      </ToggleContainer>
    </>
  );
}
